/**
 * bootstrapAlphaFunctions
 * -----------------------------------------------------------------------------
 * One-time initialization for all Alpha custom functions in Excel.
 *
 * What it does (in order):
 * 1) **Register key schemas** with `KeyBuilder` for each function family.
 *    This defines the stable cache-key shapes used across the add-in.
 * 2) **Initialize the location tracker** (`FunctionLocationTracker`) and attach
 *    worksheet-rename handlers for stable mapping of formulas to cells.
 * 3) **Warm the function call cache** (`FunctionCallCache`) and optionally
 *    enable **render-plugin mode** (reads user token and sets up first-calc
 *    auto refresh behavior).
 * 4) **Wire delegates** so exported custom functions call their real
 *    implementations (share price, currency, market data, etc.).
 * 5) **Trigger a full workbook calculation** to ensure formulas evaluate at
 *    least once after bootstrap.
 *
 * Parameters:
 * - `renderPluginMode` (default `false`): when `true`, enables special behavior
 *   used by the rendering plugin (e.g., post-first-calculation refresh).
 *
 * Side effects & environment:
 * - Uses Office.js Excel APIs (`Excel.run`, `application.calculate`, events).
 * - Registers global schemas; calling this multiple times in the same session
 *   will throw on duplicate `KeyBuilder.register(...)`.
 *
 * Typical usage:
 * ```ts
 * // at add-in startup
 * await bootstrapAlphaFunctions(process.env.RENDER_PLUGIN === '1');
 * ```
 *
 * Notes:
 * - Call exactly **once** per host session to avoid duplicate schema errors.
 * - The final full calculation is synchronous with respect to the `Excel.run`
 *   context and ensures the workbook is up to date after wiring.
 */
import { FunctionLocationTracker } from './helpers/functionLocationTracker';
import { FunctionCallCache } from './helpers/functionCallCache';
import { KeyBuilder } from './helpers/keyBuilder';
import { setSharePriceDelegate, sharePriceDataImpl } from './functions/sharePriceData';
import { currencyImpl, setCurrencyDelegate } from './functions/currency';
import { otherMarketDataImpl, setOtherMarketDataDelegate } from './functions/otherMarketData';
import { capitalizationActualsImpl, setCapitalizationActualsDelegate } from './functions/capitalizationActuals';
import { companyDataImpl, setCompanyDataDelegate } from './functions/companyData';
import { customBuiltIndexImpl, setCustomBuiltIndexDelegate } from './functions/customBuiltIndex';
import { capitalizationSummaryImpl, setCapitalizationSummaryDelegate } from './functions/capitalizationSummary';
import { marketDerivedDataImpl, setMarketDerivedDataDelegate } from './functions/marketDerivedData';
import { HttpClient } from './helpers/httpClient';
export async function bootstrapAlphaFunctions({ httpClient, renderPluginMode = false } = {}) {
    FunctionCallCache.getInstance();
    await FunctionCallCache.setRenderPluginMode(renderPluginMode);
    const tracker = FunctionLocationTracker.getInstance();
    await tracker.init();
    if (httpClient) {
        HttpClient.setAxiosClient(httpClient);
    }
    KeyBuilder.register('share_price_data', {
        req: ['symbol', 'dataType', 'date'],
        opt: ['currency'],
    });
    KeyBuilder.register('company_data', {
        req: ['symbol', 'dataType', 'currency'],
        opt: [],
    });
    KeyBuilder.register('currency', {
        req: ['symbol', 'dataType', 'date'],
        opt: [],
    });
    KeyBuilder.register('other_market_data', {
        req: ['symbol', 'dataType'],
        opt: ['date', 'endDate'],
    });
    KeyBuilder.register('capitalization_actuals', {
        req: ['dataType', 'dataSource', 'symbol', 'date'],
        opt: [],
    });
    KeyBuilder.register('custom_built_index', {
        req: ['dataType', 'date', 'inputs'],
        opt: [],
    });
    KeyBuilder.register('capitalization_summary', {
        req: ['symbol', 'source', 'dataType', 'currency', 'date'],
        opt: [],
    });
    KeyBuilder.register('market_derived_data', {
        req: ['symbol', 'dataType', 'startDate', 'endDate', 'currency'],
        opt: [],
    });
    setSharePriceDelegate(sharePriceDataImpl);
    setCompanyDataDelegate(companyDataImpl);
    setCurrencyDelegate(currencyImpl);
    setOtherMarketDataDelegate(otherMarketDataImpl);
    setCapitalizationActualsDelegate(capitalizationActualsImpl);
    setCustomBuiltIndexDelegate(customBuiltIndexImpl);
    setCapitalizationSummaryDelegate(capitalizationSummaryImpl);
    setMarketDerivedDataDelegate(marketDerivedDataImpl);
    await Excel.run(async (ctx) => {
        ctx.workbook.application.calculate(Excel.CalculationType.full);
        await ctx.sync();
    });
}
//# sourceMappingURL=bootstrap.js.map